import { REDIRECTS } from "./PromptActionsConstants";
import { DefaultDeviceOptions } from "./DevicesConstants";
import { PromptTypes } from "./PromoTypesConstants";

// ─── Path-type aliases ────────────────────────────────────────────────────────
const P = {
  modal: PromptTypes.retention_modal.value,
  video: PromptTypes.video.value,
  widget: PromptTypes.widget.value,
  banner: PromptTypes.bottom_banner.value,
  interstitial: PromptTypes.interstitial.value,
  horizontal: PromptTypes.horizontal.value,
  vertical: PromptTypes.vertical.value,
  tile: PromptTypes.tile.value,
  text: PromptTypes.text.value,
  email: PromptTypes.email.value,
  push: PromptTypes.scheduled_push.value,
};

// ─── Device aliases ───────────────────────────────────────────────────────────
const D = {
  web: DefaultDeviceOptions.web.value,
  ios: DefaultDeviceOptions.ios.value,
  tv_os: DefaultDeviceOptions.tv_os.value,
  android_os: DefaultDeviceOptions.android_os.value,
  android_tv: DefaultDeviceOptions.android_tv.value,
  roku_os: DefaultDeviceOptions.roku_os.value,
};

// ─── Path-type predicate groups ───────────────────────────────────────────────
const POPUP = [P.modal, P.video];
const WIDGET_BANNER = [P.widget, P.banner];
const INLINE = [P.horizontal, P.vertical, P.tile];
const COMMON = [...POPUP, ...WIDGET_BANNER, P.interstitial, ...INLINE];
const VISUAL = [...COMMON, P.text];
const ALL_CONTENT = [...VISUAL, P.email];
const BANNER_DEEPLINK_DEVICES = [D.android_os, D.android_tv, D.roku_os];

// ─── Rule builder ─────────────────────────────────────────────────────────────
// allow(...pathTypes) returns a (deviceType, pathType) => boolean predicate.
// Chain .onlyWeb() / .notWeb() / .onDevice(...devices) to add a device constraint.
function allow(...pathTypes) {
  const pathSet = new Set(pathTypes.flat());
  let deviceCheck = null;

  const fn = (deviceType, pathType) => {
    if (!pathSet.has(pathType)) return false;
    return deviceCheck ? deviceCheck(deviceType) : true;
  };

  fn.onlyWeb = () => {
    deviceCheck = d => d === D.web;
    return fn;
  };
  fn.notWeb = () => {
    deviceCheck = d => d !== D.web;
    return fn;
  };
  fn.onDevice = (...devices) => {
    const set = new Set(devices.flat());
    deviceCheck = d => set.has(d);
    return fn;
  };

  return fn;
}

// paths (surveyEnabled guards path_type), so no editor regression; clipboard paste from
// inline paths loses survey fields, which is the desired behavior.
const SURVEY_DEVICES = new Set([D.web, D.ios, D.tv_os, D.android_os, D.android_tv, D.roku_os]);
const surveyAllow = allow(POPUP, P.interstitial);
const surveyOptionCompatible = (d, pt) =>
  pt === P.widget || (surveyAllow(d, pt) && SURVEY_DEVICES.has(d));

export const EXACT_KEY_RULES = {
  // ─── Banner / widget positioning ──────────────────────────────────────────
  rf_banner_display: allow(P.banner),
  rf_banner_position_offset_x: allow(P.widget, P.banner),
  rf_banner_position_offset_y: allow(P.widget, P.banner),
  rf_banner_position_reverse: allow(P.banner),
  rf_mobile_banner_position_offset_y: allow(P.widget, P.banner),
  rf_mobile_height: allow(P.widget, P.banner),
  rf_mobile_message_width: allow(P.widget, P.banner),
  rf_mobile_widget_position: allow(P.widget),
  rf_mobile_width: allow(P.widget, P.banner, P.email),
  rf_widget_height: allow(P.widget, P.banner),
  rf_widget_position: allow(P.widget, P.banner),
  rf_widget_width: allow(P.widget, P.banner, P.email),

  // ─── Content / copy ────────────────────────────────────────────────────────
  rf_retention_title: allow(ALL_CONTENT, P.push),
  rf_mobile_title: allow(VISUAL),
  rf_retention_message: allow(ALL_CONTENT, P.push),
  rf_mobile_message: allow(VISUAL),
  rf_retention_acceptance_text: allow(ALL_CONTENT),
  rf_retention_align: allow(ALL_CONTENT, P.push),
  rf_retention_img_align: allow(ALL_CONTENT),
  rf_retention_decline_text: allow(COMMON, P.email),
  rf_retention_button1_text: allow(COMMON, P.email),
  rf_retention_button2_text: allow(COMMON, P.email),
  rf_retention_button3_text: allow(COMMON, P.email),

  // ─── Button styling ────────────────────────────────────────────────────────
  rf_retention_button_border_color: allow(COMMON, P.email),
  rf_retention_button_border_radius: allow(COMMON, P.email),
  rf_retention_button_border_thickness: allow(COMMON, P.email),
  rf_email_button_radius: allow(COMMON, P.email),
  // Font family: android + ios when interstitial only (per RfRetentionActionsInterstitial)
  rf_retention_button_font_family: allow(P.interstitial).onDevice(
    D.android_os,
    D.android_tv,
    D.ios,
  ),
  // Font size: interstitial or retention_modal (isInterstitialOrPopUp in component)
  rf_retention_button_font_size: allow(P.interstitial, P.modal),

  // ─── Email-specific ────────────────────────────────────────────────────────
  rf_email_body_bgcolor: allow(P.email),
  rf_email_company_name: allow(P.email),
  rf_email_content_bgcolor: allow(P.email),
  rf_email_footer_bgcolor: allow(P.email),
  rf_email_footer_font_color: allow(P.email),
  rf_email_subject: allow(P.email),

  // ─── Layout sub-objects ────────────────────────────────────────────────────
  rf_retention: allow(P.modal, P.video, P.interstitial),
  rf_retention_box: allow(P.modal, P.video, P.interstitial),
  rf_mobile: allow(P.modal, P.video, P.interstitial),
  rf_metadata: allow(VISUAL),

  // ─── Push notifications ────────────────────────────────────────────────────
  rf_push_action: allow(P.push).notWeb(),
  rf_push_ios_image: allow(P.push).onDevice(D.ios),
  rf_push_ios_image_filename: allow(P.push).onDevice(D.ios),
  rf_push_ios_image_url: allow(P.push).onDevice(D.ios),

  // ─── Countdown timer (web-only) ────────────────────────────────────────────
  rf_retention_countdown_enabled: allow(POPUP, P.banner, INLINE, P.text).onlyWeb(),
  rf_retention_countdown_end_datetime: allow(POPUP, P.banner, INLINE, P.text).onlyWeb(),
  rf_retention_countdown_position: allow(POPUP, P.banner, INLINE, P.text).onlyWeb(),
  rf_retention_countdown_size: allow(POPUP, P.banner, INLINE, P.text).onlyWeb(),
  rf_retention_countdown_start_caption: allow(POPUP, P.banner, INLINE, P.text).onlyWeb(),

  // ─── Form inputs (widget = all devices; modal/interstitial/inline = web-only) ─
  rf_retention_promo_input_1_label: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),
  rf_retention_promo_input_1_placeholder: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),
  rf_retention_promo_input_1_type: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),
  rf_retention_promo_input_1_width: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),
  rf_retention_promo_input_2_label: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),
  rf_retention_promo_input_2_placeholder: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),
  rf_retention_promo_input_2_type: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),
  rf_retention_promo_input_2_width: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),
  rf_retention_promo_input_3_label: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),
  rf_retention_promo_input_3_placeholder: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),
  rf_retention_promo_input_3_type: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),
  rf_retention_promo_input_3_width: (d, pt) =>
    pt === P.widget || (allow(POPUP, P.interstitial, INLINE)(d, pt) && d === D.web),

  // ─── Survey options ───────────────────────────────────────────────────────
  rf_retention_survey_option_1_label: surveyOptionCompatible,
  rf_retention_survey_option_1_value: surveyOptionCompatible,
  rf_retention_survey_option_2_label: surveyOptionCompatible,
  rf_retention_survey_option_2_value: surveyOptionCompatible,
  rf_retention_survey_option_3_label: surveyOptionCompatible,
  rf_retention_survey_option_3_value: surveyOptionCompatible,
  rf_retention_survey_option_4_label: surveyOptionCompatible,
  rf_retention_survey_option_4_value: surveyOptionCompatible,
  rf_retention_survey_option_5_label: surveyOptionCompatible,
  rf_retention_survey_option_5_value: surveyOptionCompatible,
  rf_retention_survey_options_font_size: surveyOptionCompatible,
  rf_retention_survey_options_total: surveyOptionCompatible,
  rf_retention_survey_selected: surveyOptionCompatible,
  rf_retention_survey_top_margin: surveyOptionCompatible,

  // ─── Typography ───────────────────────────────────────────────────────────
  rf_settings_message_font_size: allow(ALL_CONTENT, P.push),
  rf_settings_mobile_message_font_size: allow(VISUAL),
  rf_settings_title_font_size: allow(ALL_CONTENT, P.push),
  rf_settings_mobile_title_font_size: allow(VISUAL),
  rf_settings_title_padding_top: allow(ALL_CONTENT, P.push),
  rf_settings_mobile_title_padding_top: allow(VISUAL),
  rf_settings_body_padding_side: allow(ALL_CONTENT, P.push),
  // Message width: tile and vertical hide the Size panel; horizontal is the only inline that shows it
  rf_settings_text_container_max_width: allow(POPUP, WIDGET_BANNER, P.interstitial, P.horizontal),

  // ─── Color / fill ─────────────────────────────────────────────────────────
  rf_settings_fill_color: allow(ALL_CONTENT, P.push),

  // ─── Layout / sizing ──────────────────────────────────────────────────────
  rf_settings_bar_position: allow(INLINE),
  rf_settings_overlay_background: allow(P.modal, P.video, P.interstitial),
  // Pop-up size: non-web only; interstitial uses a fixed layout (showPopUpSize is not passed there)
  rf_settings_pop_up_size: allow(POPUP).notWeb(),
  // Window max-width: web only (non-web devices use rf_settings_pop_up_size instead)
  rf_settings_window_max_width: allow(P.modal, P.video, P.interstitial).onlyWeb(),

  // ─── Close / behavior timing ──────────────────────────────────────────────
  rf_settings_close_seconds: allow(VISUAL),
  rf_settings_close_seconds_text: allow(VISUAL),
  rf_settings_timeout_interval: allow(VISUAL),
  rf_settings_accept_interval: allow(ALL_CONTENT),
  rf_settings_decline_interval: allow(COMMON, P.email),
  rf_settings_dismiss_interval: allow(ALL_CONTENT),
  rf_settings_delay_fadein_seconds: allow(POPUP, P.banner).onlyWeb(),
  rf_settings_delay_fadeout_seconds: allow(POPUP, P.banner).onlyWeb(),
  rf_settings_hide_timer_text: allow(VISUAL),
  rf_settings_timer_font_color: allow(VISUAL),
  rf_settings_timer_font_size: allow(VISUAL),
  rf_settings_disable_click: allow(P.modal, P.video, P.interstitial),
  rf_settings_click_outside_close_enabled: allow(COMMON, P.email),
  rf_settings_close_button_enabled: allow(ALL_CONTENT),
  rf_settings_cancel_button_enabled: allow(COMMON, P.email),
  rf_settings_confirm_button_2_enabled: allow(COMMON, P.email),

  // ─── Background images ────────────────────────────────────────────────────
  rf_settings_bg_image: () => true,
  rf_settings_bg_image_color: allow(ALL_CONTENT),
  rf_settings_bg_image_filename: allow(ALL_CONTENT, P.push),
  rf_settings_bg_image_is_url: allow(ALL_CONTENT, P.push),
  rf_settings_bg_image_url: allow(P.push),
  rf_settings_mobile_bg_image: () => true,
  rf_settings_mobile_bg_image_filename: allow(ALL_CONTENT),
  rf_settings_mobile_bg_image_is_url: allow(ALL_CONTENT),

  // ─── Video ────────────────────────────────────────────────────────────────
  rf_settings_video_src: allow(P.video),
  rf_settings_video_src_upload_url: allow(P.video),
  rf_settings_video_loop: allow(P.video),
  rf_settings_video_muted: allow(P.video),
  rf_settings_video_poster: allow(P.video),
  rf_settings_video_autoplayed: allow(P.video),
  rf_settings_video_filename: allow(P.video),
  rf_settings_video_is_url: allow(P.video),
  rf_settings_video_media_type: allow(P.video),
  // Mobile video: modal/video path on non-web devices
  rf_settings_mobile_video_src: allow(POPUP).notWeb(),
  rf_settings_mobile_video_src_upload_url: allow(POPUP).notWeb(),
  rf_settings_mobile_video_loop: allow(POPUP).notWeb(),
  rf_settings_mobile_video_muted: allow(POPUP).notWeb(),
  rf_settings_mobile_video_poster: allow(POPUP).notWeb(),
  rf_settings_mobile_video_autoplayed: allow(POPUP).notWeb(),
  rf_settings_mobile_video_filename: allow(POPUP).notWeb(),
  rf_settings_mobile_video_is_url: allow(POPUP).notWeb(),
  rf_settings_mobile_video_media_type: allow(POPUP).notWeb(),

  // ─── CSS ─────────────────────────────────────────────────────────────────
  rf_settings_custom_css: allow(ALL_CONTENT),

  // ─── Tile / inline interaction ────────────────────────────────────────────
  rf_settings_tile_interaction: (d, pt) =>
    (pt === P.modal && d === D.web) || allow(P.widget, P.interstitial, INLINE, P.text)(d, pt),
  rf_settings_tile_is_responsive: allow(ALL_CONTENT),
  rf_settings_permanent_display: allow(INLINE, P.text),

  // ─── Paywall (web-only) ───────────────────────────────────────────────────
  rf_retention_paywall_css: allow(VISUAL).onlyWeb(),
  rf_retention_paywall_limit: allow(VISUAL).onlyWeb(),
  rf_retention_paywall_strip_text_selector: allow(VISUAL).onlyWeb(),
  rf_retention_paywall_tracker_id: allow(VISUAL).onlyWeb(),
  rf_retention_paywall_zone: allow(VISUAL).onlyWeb(),

  // ─── Legal ────────────────────────────────────────────────────────────────
  // Excluded on TV/streaming platforms
  rf_settings_privacy_policy_text: allow(P.modal, P.interstitial).onDevice(
    D.web,
    D.ios,
    D.android_os,
  ),
  rf_settings_privacy_policy_link: allow(P.email),
  rf_settings_tos_link: allow(P.email),

  // ─── Billing ──────────────────────────────────────────────────────────────
  rf_billing_update_enabled: (d, pt) =>
    (allow(POPUP)(d, pt) && d === D.web) || allow(P.widget, P.interstitial, INLINE)(d, pt),

  // ─── In-App Purchase (path type + device both matter) ─────────────────────
  rf_settings_apple_inapp_product_id: allow(
    POPUP,
    P.banner,
    P.interstitial,
    INLINE,
    P.text,
    P.push,
  ).onDevice(D.ios, D.tv_os),
  rf_settings_android_inapp_product_id: allow(
    POPUP,
    P.banner,
    P.interstitial,
    INLINE,
    P.text,
    P.push,
  ).onDevice(D.android_os, D.android_tv),
  rf_settings_roku_product_id: allow(
    POPUP,
    P.banner,
    P.interstitial,
    INLINE,
    P.text,
    P.push,
  ).onDevice(D.roku_os),
  rf_settings_roku_product_operation: allow(
    POPUP,
    P.banner,
    P.interstitial,
    INLINE,
    P.text,
    P.push,
  ).onDevice(D.roku_os),

  // ─── Deep link ────────────────────────────────────────────────────────────
  // Modal/interstitial/inline/text: non-web only
  // Banner: android + roku only (not ios, not web)
  // Push: always
  rf_settings_deeplink: (d, pt) =>
    allow(POPUP, P.interstitial, INLINE, P.text).notWeb()(d, pt) ||
    (pt === P.banner && BANNER_DEEPLINK_DEVICES.includes(d)) ||
    pt === P.push,

  // ─── Push prompts on open ─────────────────────────────────────────────────
  rf_settings_apple_prompt_on_open: allow(P.push),
  rf_settings_android_prompt_on_open: allow(P.push),
};

// ─── Layout categories ────────────────────────────────────────────────────────
// Some properties carry structural meaning (e.g. "message width inside a popup window"
// vs "message column width in an inline tile") that differs across layout categories.
// Copying them across categories produces wrong dimensions even though the property key
// exists on both target types.  CATEGORY_SENSITIVE_KEYS lists such properties; they are
// only transferred when source and target belong to the same layout category.
const LAYOUT_CATEGORIES = [
  [P.modal, P.video, P.interstitial], // popup: full-screen overlay with a window frame
  [P.widget, P.banner], // widget / banner: anchored floating element
  [P.horizontal, P.vertical, P.tile, P.text], // inline: embedded in page content
  [P.email],
  [P.push],
];

function getLayoutCategory(pathType) {
  return LAYOUT_CATEGORIES.findIndex(types => types.includes(pathType));
}

// Keys whose numeric value is only meaningful within the same layout category.
export const CATEGORY_SENSITIVE_KEYS = new Set(["rf_settings_text_container_max_width"]);

/**
 * Returns false when `propKey` is category-sensitive and source/target are in different
 * layout categories (e.g. copying an inline "message column width" into a popup).
 * Returns true when either path type is unknown or the key is not category-sensitive.
 */
export function isActionPropertyCrossCompatible(propKey, sourcePathType, targetPathType) {
  if (!CATEGORY_SENSITIVE_KEYS.has(propKey)) return true;
  const sourceCat = getLayoutCategory(sourcePathType);
  const targetCat = getLayoutCategory(targetPathType);
  return sourceCat === -1 || targetCat === -1 || sourceCat === targetCat;
}

// ─── Prefix rules ─────────────────────────────────────────────────────────────
// Applied (longest-prefix-first) when no exact key match is found.
// Order matters — more specific prefixes (rf_push_ios_) must come before general ones (rf_push_).
export const PREFIX_RULES = [
  { prefix: "rf_push_ios_", compatible: allow(P.push).onDevice(D.ios) },
  { prefix: "rf_push_", compatible: allow(P.push).notWeb() },
  { prefix: "rf_widget_", compatible: allow(P.widget, P.banner) },
  { prefix: "rf_email_", compatible: allow(P.email) },
  { prefix: "rf_banner_", compatible: allow(P.widget, P.banner) },
  { prefix: "rf_mobile_", compatible: allow(P.modal, P.video, P.interstitial) },
  // Catch-all for rf_settings_* keys not covered by exact rules
  { prefix: "rf_settings_", compatible: allow(ALL_CONTENT) },
];

const UNIVERSAL_KEYS = new Set(REDIRECTS);

export function isActionPropertyCompatible(propKey, deviceType, pathType) {
  if (!deviceType || !pathType) return true;
  if (UNIVERSAL_KEYS.has(propKey)) return true;
  if (Object.prototype.hasOwnProperty.call(EXACT_KEY_RULES, propKey)) {
    return EXACT_KEY_RULES[propKey](deviceType, pathType);
  }
  for (const { prefix, compatible } of PREFIX_RULES) {
    if (propKey.startsWith(prefix)) {
      return compatible(deviceType, pathType);
    }
  }
  return true;
}
